import { useState, useEffect, useMemo } from "react";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, Legend, BarChart, Bar,
  CartesianGrid, ResponsiveContainer, PieChart, Pie, Cell
} from "recharts";

export default function ButtonPressAnalytics() {
  const [events, setEvents] = useState([]);
  const [reportDefs, setReportDefs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState({
    startDate: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 7 days ago
    endDate: new Date().toISOString().split('T')[0] // today
  });
  const [selectedSerials, setSelectedSerials] = useState('all');
  const [viewType, setViewType] = useState('timeline'); // timeline, scores, distribution

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [eventsRes, reportsRes] = await Promise.all([
          fetch(process.env.REACT_APP_BACKEND_URL+"/button_press_data"),
          fetch(process.env.REACT_APP_BACKEND_URL+"/button_report_definitions")
        ]);

        const eventsData = await eventsRes.json();
        const reportsData = await reportsRes.json();

        setEvents(Array.isArray(eventsData) ? eventsData : []);
        setReportDefs(Array.isArray(reportsData) ? reportsData : []);
      } catch (err) {
        console.error("Failed to fetch analytics data:", err);
        setEvents([]);
        setReportDefs([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Filter and process data based on date range
  const filteredData = useMemo(() => {
    const startTimestamp = new Date(dateRange.startDate).getTime() / 1000;
    const endTimestamp = new Date(dateRange.endDate + 'T23:59:59').getTime() / 1000;

    return events.filter(event => 
      event.timestamp >= startTimestamp && 
      event.timestamp <= endTimestamp &&
      (selectedSerials === 'all' || event.serial === selectedSerials)
    );
  }, [events, dateRange, selectedSerials]);

  // Get unique serial numbers for filter dropdown
  const uniqueSerials = useMemo(() => {
    return [...new Set(events.map(e => e.serial))].sort();
  }, [events]);

  // Process timeline data
  const timelineData = useMemo(() => {
    if (!filteredData.length) return [];

    // Group by date and serial
    const groupedData = {};
    
    filteredData.forEach(event => {
      const date = new Date(event.timestamp * 1000).toLocaleDateString();
      const def = reportDefs.find(r => r.serial === event.serial && r.button_id === event.button_id);
      
      if (!def) return;

      const key = selectedSerials === 'all' ? `${date}_${event.serial}` : date;
      
      if (!groupedData[key]) {
        groupedData[key] = {
          date: date,
          serial: event.serial,
          good: 0,
          neutral: 0,
          bad: 0,
          cleanup: 0,
          supplies1: 0,
          supplies2: 0,
          score: 0
        };
      }

      if (def.report_type === 1) { // Service quality
        if (event.button_id === 1) {
          groupedData[key].good += 1;
          groupedData[key].score += 100;
        } else if (event.button_id === 2) {
          groupedData[key].neutral += 1;
        } else if (event.button_id === 3) {
          groupedData[key].bad += 1;
          groupedData[key].score -= 100;
        }
      } else if (def.report_type === 2) { // Supplies/requests
        if (event.button_id === 1) {
          groupedData[key].cleanup += 1;
        } else if (event.button_id === 2) {
          groupedData[key].supplies1 += 1;
        } else if (event.button_id === 3) {
          groupedData[key].supplies2 += 1;
        }
      }
    });

    return Object.values(groupedData).sort((a, b) => new Date(a.date) - new Date(b.date));
  }, [filteredData, reportDefs, selectedSerials]);

  // Calculate service quality scores by serial
  const serviceQualityScores = useMemo(() => {
    const scores = {};
    
    filteredData.forEach(event => {
      const def = reportDefs.find(r => r.serial === event.serial && r.button_id === event.button_id);
      
      if (!def || def.report_type !== 1) return;

      if (!scores[event.serial]) {
        scores[event.serial] = { serial: event.serial, score: 0, total: 0 };
      }

      scores[event.serial].total += 1;
      
      if (event.button_id === 1) {
        scores[event.serial].score += 100;
      } else if (event.button_id === 3) {
        scores[event.serial].score -= 100;
      }
    });

    return Object.values(scores).sort((a, b) => b.score - a.score);
  }, [filteredData, reportDefs]);

  // Distribution data for pie charts
  const serviceDistribution = useMemo(() => {
    const counts = { good: 0, neutral: 0, bad: 0 };
    
    filteredData.forEach(event => {
      const def = reportDefs.find(r => r.serial === event.serial && r.button_id === event.button_id);
      
      if (!def || def.report_type !== 1) return;

      if (event.button_id === 1) counts.good += 1;
      else if (event.button_id === 2) counts.neutral += 1;
      else if (event.button_id === 3) counts.bad += 1;
    });

    return [
      { name: 'Laba kvalitāte', value: counts.good, color: '#10b981' },
      { name: 'Neitrāla kvalitāte', value: counts.neutral, color: '#f59e0b' },
      { name: 'Slikta kvalitāte', value: counts.bad, color: '#ef4444' }
    ].filter(item => item.value > 0);
  }, [filteredData, reportDefs]);

  const requestsDistribution = useMemo(() => {
    const counts = { cleanup: 0, supplies1: 0, supplies2: 0 };
    
    filteredData.forEach(event => {
      const def = reportDefs.find(r => r.serial === event.serial && r.button_id === event.button_id);
      
      if (!def || def.report_type !== 2) return;

      if (event.button_id === 1) counts.cleanup += 1;
      else if (event.button_id === 2) counts.supplies1 += 1;
      else if (event.button_id === 3) counts.supplies2 += 1;
    });

    return [
      { name: 'Nepieciešama uzkopšana', value: counts.cleanup, color: '#3b82f6' },
      { name: 'Nepieciešamas piegādes 1', value: counts.supplies1, color: '#8b5cf6' },
      { name: 'Nepieciešamas piegādes 2', value: counts.supplies2, color: '#06b6d4' }
    ].filter(item => item.value > 0);
  }, [filteredData, reportDefs]);

  if (loading) return <p className="p-4">Datu ielāde...</p>;

  const renderTimelineView = () => (
    <div className="space-y-6">
      <div className="bg-white p-6 rounded-lg shadow">
        <h3 className="text-lg font-semibold mb-4">Servisa kvalitātes novērtējumi laika gaitā</h3>
        <ResponsiveContainer width="100%" height={400}>
          <LineChart data={timelineData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="date" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Line type="monotone" dataKey="good" stroke="#10b981" strokeWidth={2} name="Laba kvalitāte" />
            <Line type="monotone" dataKey="neutral" stroke="#f59e0b" strokeWidth={2} name="Neitrāla kvalitāte" />
            <Line type="monotone" dataKey="bad" stroke="#ef4444" strokeWidth={2} name="Slikta kvalitāte" />
          </LineChart>
        </ResponsiveContainer>
      </div>

      <div className="bg-white p-6 rounded-lg shadow">
        <h3 className="text-lg font-semibold mb-4">Pieprasījumi laika gaitā</h3>
        <ResponsiveContainer width="100%" height={400}>
          <LineChart data={timelineData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="date" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Line type="monotone" dataKey="cleanup" stroke="#3b82f6" strokeWidth={2} name="Uzkopšana" />
            <Line type="monotone" dataKey="supplies1" stroke="#8b5cf6" strokeWidth={2} name="Piegādes 1" />
            <Line type="monotone" dataKey="supplies2" stroke="#06b6d4" strokeWidth={2} name="Piegādes 2" />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );

  const renderScoresView = () => (
    <div className="space-y-6">
      <div className="bg-white p-6 rounded-lg shadow">
        <h3 className="text-lg font-semibold mb-4">Servisa kvalitātes rezultāti pa sērijas numuriem</h3>
        <ResponsiveContainer width="100%" height={400}>
          <BarChart data={serviceQualityScores}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="serial" />
            <YAxis />
            <Tooltip formatter={(value, name) => [value, name === 'score' ? 'Rezultāts' : name]} />
            <Bar dataKey="score" fill="#8884d8" name="Kvalitātes rezultāts" />
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="bg-white p-6 rounded-lg shadow">
        <h3 className="text-lg font-semibold mb-4">Kvalitātes rezultātu laika grafiks</h3>
        <ResponsiveContainer width="100%" height={400}>
          <LineChart data={timelineData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="date" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Line type="monotone" dataKey="score" stroke="#8884d8" strokeWidth={3} name="Kvalitātes rezultāts" />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );

  const renderDistributionView = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <div className="bg-white p-6 rounded-lg shadow">
        <h3 className="text-lg font-semibold mb-4">Servisa kvalitātes sadalījums</h3>
        {serviceDistribution.length > 0 ? (
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={serviceDistribution}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                outerRadius={80}
                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(1)}%`}
              >
                {serviceDistribution.map((entry, index) => (
                  <Cell key={index} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        ) : (
          <p className="text-gray-500">Nav datu par servisa kvalitāti</p>
        )}
      </div>

      <div className="bg-white p-6 rounded-lg shadow">
        <h3 className="text-lg font-semibold mb-4">Pieprasījumu sadalījums</h3>
        {requestsDistribution.length > 0 ? (
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={requestsDistribution}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                outerRadius={80}
                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(1)}%`}
              >
                {requestsDistribution.map((entry, index) => (
                  <Cell key={index} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        ) : (
          <p className="text-gray-500">Nav datu par pieprasījumiem</p>
        )}
      </div>
    </div>
  );

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto">
        <h1 className="text-3xl font-bold text-gray-900 mb-6">Pogu nospiešanas analīze</h1>
        
        {/* Controls */}
        <div className="bg-white p-6 rounded-lg shadow mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Sākuma datums</label>
              <input
                type="date"
                value={dateRange.startDate}
                onChange={(e) => setDateRange({...dateRange, startDate: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Beigu datums</label>
              <input
                type="date"
                value={dateRange.endDate}
                onChange={(e) => setDateRange({...dateRange, endDate: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Sērijas numurs</label>
              <select
                value={selectedSerials}
                onChange={(e) => setSelectedSerials(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">Visi</option>
                {uniqueSerials.map(serial => (
                  <option key={serial} value={serial}>{serial}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Skatījums</label>
              <select
                value={viewType}
                onChange={(e) => setViewType(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="timeline">Laika grafiks</option>
                <option value="scores">Kvalitātes rezultāti</option>
                <option value="distribution">Sadalījums</option>
              </select>
            </div>
          </div>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-medium text-gray-500">Kopējie notikumi</h3>
            <p className="text-2xl font-bold text-gray-900">{filteredData.length}</p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-medium text-gray-500">Aktīvie sērijas numuri</h3>
            <p className="text-2xl font-bold text-gray-900">
              {selectedSerials === 'all' ? uniqueSerials.length : 1}
            </p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-medium text-gray-500">Vidējais kvalitātes rezultāts</h3>
            <p className="text-2xl font-bold text-gray-900">
              {serviceQualityScores.length > 0 
                ? Math.round(serviceQualityScores.reduce((sum, s) => sum + s.score, 0) / serviceQualityScores.length)
                : 0}
            </p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-medium text-gray-500">Dienu skaits</h3>
            <p className="text-2xl font-bold text-gray-900">
              {Math.ceil((new Date(dateRange.endDate) - new Date(dateRange.startDate)) / (1000 * 60 * 60 * 24)) + 1}
            </p>
          </div>
        </div>

        {/* Charts */}
        {viewType === 'timeline' && renderTimelineView()}
        {viewType === 'scores' && renderScoresView()}
        {viewType === 'distribution' && renderDistributionView()}
      </div>
    </div>
  );
}